import format from 'string-template';
import { toJS } from 'mobx';
import { makeObservable, observable, action, computed, reaction } from 'mobx';
import { getFromLocalStorage, saveToLocalStorage } from '/@/utils/localStorage';
import { numberOrNull } from '/@/app/utils';
import { LOCAL_STORAGE_KEY, LOCAL_STORAGE_KEY_VERIFY_ERROR } from './localStorage';


export class ValueState {
    // Constant fields
    panelId;
    caption;

    localStorageKey;
    localStorageKeyVerifyError;

    // Auxiliary fields
    saveState;

    // Dynamic fields
    value;   // currently visible & stored locally
    savedValue;  // saved to server
    automaticValue;  // value from the server

    constructor(value, panelId) {
        this.panelId = panelId;
        this.caption = value.field.caption;
        this.savedValue = numberOrNull(value.value);
        this.automaticValue = numberOrNull(value.value);
        if (value.manual_value !== null) {
            this.savedValue = numberOrNull(value.manual_value);
        }

        // Set value or load from local storage
        let _value = this.savedValue;
        this.localStorageKey = format(LOCAL_STORAGE_KEY, {
            panelId: this.panelId,
            id: this.caption,  // using caption as id
        });
        this.localStorageKeyVerifyError = format(LOCAL_STORAGE_KEY_VERIFY_ERROR, {
            panelId: this.panelId,
            id: this.caption,  // using caption as id
        });

        const _localValue = getFromLocalStorage(this.localStorageKey, null);
        if (_localValue !== null) {
            _value = _localValue;
        }
        this.value = _value;
        // Don't verify on page load, it can cause infinite error loops [FIXME investigate it]
        this.updateSaveState(_value, true);
        if (this.saveState !== "saved" && getFromLocalStorage(this.localStorageKeyVerifyError, false)) {
            this.saveState = 'error';
        }
        //
        makeObservable(this, {
            value: observable,
            automaticValue: observable,
            savedValue: observable,
            saveState: observable,
            //
            setInputValue: action,
            setValue: action,
            //
            setSavedValue: action,
            setSaveState: action,
            //
            updateSaveState: action,
        });

        // Save the value locally on change.
        reaction(() => this.value, (value) => {
            saveToLocalStorage(this.localStorageKey, value);
        });
    }

    updateSaveState(value, dontVerify) {
        if (dontVerify === undefined) {
            dontVerify = false;
        }

        const needsSave = this.valueNeedsSave(value);
        if (
            !needsSave
            // FIXME when/if value types will be introduced, organize conversion of strings to floats here
            || !this.savedValue && !value
        ) {
            this.setSaveState("saved");
        } else {
            this.setSaveState("unsaved");
            if (!dontVerify) {
                this.scheduleVerify();
            }
        }
    }

    setInputValue(newValue) {
        newValue = numberOrNull(parseFloat(newValue));
        this.value = newValue;
        this.updateSaveState(this.value);
    }
    setValue(value) {
        this.value = value;
        this.updateSaveState(this.value);
    }

    setSavedValue(value) {
        this.savedValue = value;
    }

    setSaveState(state) {
        if (!["saved", "unsaved", "verified", "error"].includes(state)) {
            throw new Error(`Invalid save state "${state}"`);
        }
        this.saveState = state;
        //
        if (["saved", "verified"].includes(state)) {
            saveToLocalStorage(this.localStorageKeyVerifyError, false);
        }
        if (state === "error") {
            saveToLocalStorage(this.localStorageKeyVerifyError, true);
        }
    }

    VERIFY_DELAY = 500;  // ms
    verifyTimeoutId = null;

    scheduleVerify(delay) {
        if (delay === undefined) {
            delay = this.VERIFY_DELAY;
        }

        if (this.verifyTimeoutId) {
            clearTimeout(this.verifyTimeoutId);
        }
        this.verifyTimeoutId = setTimeout(() => {
            this.verify();
        }, delay);
    }

    cancelVerify() {
        if (this.verifyTimeoutId) {
            clearTimeout(this.verifyTimeoutId);
            this.verifyTimeoutId = null;
        }
    }

    cleanValueType() {
        let val = numberOrNull(this.value);
        if (val === null) {
            val = "";
        } else if (typeof val === 'string') {
            val = val.trim();
            val = val.replace(",", ".");  // accept Russian dot style
        }
        //
        let floatVal;
        if (val === "") {   // FIXME? verify the logic.
            floatVal = null;
        } else {
            floatVal = parseFloat(val);
        }
        //
        // Client-side check
        //
        if (isNaN(floatVal)) {
            this.setSaveState("error");
            return null;
        }

        const needsSave = this.valueNeedsSave(floatVal);
        if (!needsSave
            || !this.savedValue && !floatVal) {
            this.setSaveState("saved");
            return floatVal;
        }
        if (floatVal === null) {
            this.setSaveState("verified");
            return floatVal;
        }
        if (needsSave) {
            this.setSaveState("verified");  // still need to save.
            return floatVal;
        }
        return floatVal;
    }

    verify() {
        // FIXME verification is not important here
        // copied from MeasurementState just to keep the same look and feel
        const floatVal = this.cleanValueType()
        this.setSaveState("verified");
    }

    valueNeedsSave(value) {
        // double equality is correct here
        const valueUnChanged = this.savedValue == value;
        return !valueUnChanged;
    }
}
